import { useState, useCallback, useRef } from 'react';

export interface TimelineViewport {
  startTime: number;
  endTime: number;
  pixelsPerSecond: number;
  width: number;
}

export interface TimelineState {
  viewport: TimelineViewport;
  isAutoScroll: boolean;
  selectedTrackIds: string[];
  snapToGrid: boolean;
  gridSize: number; // in seconds
  isDragging: boolean;
  dragTrackId: string | null;
  dragStartTime: number;
  dragStartPosition: number; // original startTime of the track being dragged
}

export interface TimelineControls {
  setViewport: (viewport: Partial<TimelineViewport>) => void;
  setIsAutoScroll: (enabled: boolean) => void;
  setSelectedTracks: (trackIds: string[]) => void;
  toggleTrackSelection: (trackId: string) => void;
  clearSelection: () => void;
  setSnapToGrid: (enabled: boolean) => void;
  setGridSize: (size: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomToFit: (duration: number) => void;
  scrollToTime: (time: number) => void;
  scrollToCurrentTime: (currentTime: number) => void;
  getTimeAtPixel: (x: number) => number;
  getPixelAtTime: (time: number) => number;
  snapTimeToGrid: (time: number) => number;
  startDrag: (trackId: string, startTime: number, currentPosition: number) => void;
  updateDrag: (currentTime: number) => number; // returns the snapped time
  endDrag: () => void;
  cancelDrag: () => void;
}

const DEFAULT_PIXELS_PER_SECOND = 50;
const MIN_PIXELS_PER_SECOND = 5;
const MAX_PIXELS_PER_SECOND = 500;
const DEFAULT_GRID_SIZE = 1; // 1 second

export const useAudioTimeline = (
  initialWidth: number = 800,
  initialDuration: number = 300
): TimelineState & TimelineControls => {
  const [viewport, setViewportState] = useState<TimelineViewport>({
    startTime: 0,
    endTime: initialDuration,
    pixelsPerSecond: DEFAULT_PIXELS_PER_SECOND,
    width: initialWidth
  });

  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [selectedTrackIds, setSelectedTrackIds] = useState<string[]>([]);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(DEFAULT_GRID_SIZE);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTrackId, setDragTrackId] = useState<string | null>(null);
  const [dragStartTime, setDragStartTime] = useState(0);
  const [dragStartPosition, setDragStartPosition] = useState(0);

  const prevCurrentTimeRef = useRef<number>(0);

  const setViewport = useCallback((updates: Partial<TimelineViewport>) => {
    setViewportState(prev => {
      const newViewport = { ...prev, ...updates };
      
      // Ensure pixelsPerSecond is within bounds
      if (newViewport.pixelsPerSecond < MIN_PIXELS_PER_SECOND) {
        newViewport.pixelsPerSecond = MIN_PIXELS_PER_SECOND;
      } else if (newViewport.pixelsPerSecond > MAX_PIXELS_PER_SECOND) {
        newViewport.pixelsPerSecond = MAX_PIXELS_PER_SECOND;
      }

      // Ensure startTime is not negative
      if (newViewport.startTime < 0) {
        newViewport.startTime = 0;
      }

      // Ensure endTime is greater than startTime
      if (newViewport.endTime <= newViewport.startTime) {
        newViewport.endTime = newViewport.startTime + (newViewport.width / newViewport.pixelsPerSecond);
      }

      return newViewport;
    });
  }, []);

  const setSelectedTracks = useCallback((trackIds: string[]) => {
    setSelectedTrackIds(trackIds);
  }, []);

  const toggleTrackSelection = useCallback((trackId: string) => {
    setSelectedTrackIds(prev => 
      prev.includes(trackId) 
        ? prev.filter(id => id !== trackId)
        : [...prev, trackId]
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedTrackIds([]);
  }, []);

  const zoomIn = useCallback(() => {
    setViewport({ 
      pixelsPerSecond: Math.min(viewport.pixelsPerSecond * 1.5, MAX_PIXELS_PER_SECOND) 
    });
  }, [viewport.pixelsPerSecond, setViewport]);

  const zoomOut = useCallback(() => {
    setViewport({ 
      pixelsPerSecond: Math.max(viewport.pixelsPerSecond / 1.5, MIN_PIXELS_PER_SECOND) 
    });
  }, [viewport.pixelsPerSecond, setViewport]);

  const zoomToFit = useCallback((duration: number) => {
    const newPixelsPerSecond = Math.max(MIN_PIXELS_PER_SECOND, 
      Math.min(MAX_PIXELS_PER_SECOND, viewport.width / duration));
    
    setViewport({
      startTime: 0,
      endTime: duration,
      pixelsPerSecond: newPixelsPerSecond
    });
  }, [viewport.width, setViewport]);

  const scrollToTime = useCallback((time: number) => {
    const visibleDuration = viewport.width / viewport.pixelsPerSecond;
    const startTime = Math.max(0, time - visibleDuration / 2);
    const endTime = startTime + visibleDuration;
    
    setViewport({ startTime, endTime });
  }, [viewport.width, viewport.pixelsPerSecond, setViewport]);

  const scrollToCurrentTime = useCallback((currentTime: number) => {
    if (!isAutoScroll) return;

    const visibleDuration = viewport.width / viewport.pixelsPerSecond;
    const rightEdge = viewport.startTime + visibleDuration;
    const leftEdge = viewport.startTime;

    // Auto-scroll if current time is approaching the right edge or has moved significantly
    if (currentTime > rightEdge - visibleDuration * 0.1 || 
        currentTime < leftEdge ||
        Math.abs(currentTime - prevCurrentTimeRef.current) > visibleDuration * 0.5) {
      
      scrollToTime(currentTime);
    }

    prevCurrentTimeRef.current = currentTime;
  }, [isAutoScroll, viewport, scrollToTime]);

  const getTimeAtPixel = useCallback((x: number): number => {
    return viewport.startTime + (x / viewport.pixelsPerSecond);
  }, [viewport.startTime, viewport.pixelsPerSecond]);

  const getPixelAtTime = useCallback((time: number): number => {
    return (time - viewport.startTime) * viewport.pixelsPerSecond;
  }, [viewport.startTime, viewport.pixelsPerSecond]);

  const snapTimeToGrid = useCallback((time: number): number => {
    if (!snapToGrid) return time;
    return Math.round(time / gridSize) * gridSize;
  }, [snapToGrid, gridSize]);

  const startDrag = useCallback((trackId: string, startTime: number, currentPosition: number) => {
    setIsDragging(true);
    setDragTrackId(trackId);
    setDragStartTime(startTime);
    setDragStartPosition(currentPosition);
  }, []);

  const updateDrag = useCallback((currentTime: number): number => {
    if (!isDragging) return currentTime;
    
    const deltaTime = currentTime - dragStartTime;
    const newPosition = Math.max(0, dragStartPosition + deltaTime);
    
    // Apply grid snapping during drag for visual feedback
    return snapTimeToGrid(newPosition);
  }, [isDragging, dragStartTime, dragStartPosition, snapTimeToGrid]);

  const endDrag = useCallback(() => {
    setIsDragging(false);
    setDragTrackId(null);
    setDragStartTime(0);
    setDragStartPosition(0);
  }, []);

  const cancelDrag = useCallback(() => {
    setIsDragging(false);
    setDragTrackId(null);
    setDragStartTime(0);
    setDragStartPosition(0);
  }, []);

  return {
    // State
    viewport,
    isAutoScroll,
    selectedTrackIds,
    snapToGrid,
    gridSize,
    isDragging,
    dragTrackId,
    dragStartTime,
    dragStartPosition,

    // Controls
    setViewport,
    setIsAutoScroll,
    setSelectedTracks,
    toggleTrackSelection,
    clearSelection,
    setSnapToGrid,
    setGridSize,
    zoomIn,
    zoomOut,
    zoomToFit,
    scrollToTime,
    scrollToCurrentTime,
    getTimeAtPixel,
    getPixelAtTime,
    snapTimeToGrid,
    startDrag,
    updateDrag,
    endDrag,
    cancelDrag
  };
};